<script setup>
    const { loggedIn, user, fetch: refreshSession } = useUserSession();

    const props = defineProps({
        session: {
            type: Object,
            required: true,
        },
    });

    const isFavorite = ref(null)
    const busy = ref(false)

    const image = {
        url: 'https://cdn.builder.io/api/v1/image/assets%2Fd09cfff8ac614d8a86e457a0274fa171%2Fed07e6e152164eb9bf0abe2985c347a1',
        alt: 'An image showing friendly, happy people at the Inbound conference.',
    };

    const handleSkillClick = async (level) => {
        await navigateTo({ path: '/sessions', query: { level: level } });
    };

    const duration = computed(() => {
        if (props.session.start_date_and_time && props.session.end_date_and_time) {
            const start = new Date(props.session.start_date_and_time);
            const end = new Date(props.session.end_date_and_time);
            const diffMs = end - start;
            if (diffMs > 0) {
                const diffMins = Math.round(diffMs / 60000);
                const hours = Math.floor(diffMins / 60);
                const mins = diffMins % 60;
                const pluralMins = mins > 1 ? 'minutes' : 'minute';
                const pluralHours = hours > 1 ? 'hours' : 'hour';
                if (hours && mins) return `${hours} ${pluralHours} ${mins} ${pluralMins}`;
                if (hours) return `${hours} ${pluralHours}`;
                return `${mins} ${pluralMins}`;
            }
        }
        return null;
    });

    const location = computed(() => {
        if (typeof props.session.locations === 'string') return props.session.locations;
        if (Array.isArray(props.session.locations)) return props.session.locations[0];
        return '';
    });

    const handleFavoriteClick = async () => {
        if (!loggedIn.value) {
            await navigateTo({ path: '/login', query: { action: 'favorite' } })
            return;
        }
        try {
            busy.value = true;
            isFavorite.value = true;
            await $fetch('/api/v1/user/favorite', {
                method: 'POST',
                body: { sessionId: props.session.unique_id, email: user.value.email, ticketId: user.value.ticketId },
            });
            await refreshSession();
        } catch (error) {
            window.alert('There was an error adding this session to your favorites. Please try again later.');
            console.error('Error favoriting session:', error);
            isFavorite.value = null;
        } finally {
            busy.value = false;
        }
    };

    const is_favorited = computed(() => {
        // Check if user.favorites exists and contains the session unique_id
        const userHasFavorited = user.value?.favorites?.includes(props.session.unique_id);
        return isFavorite.value === null ? userHasFavorited : isFavorite.value;
    });

    const handleUnfavoriteClick = async () => {
        if (!loggedIn.value) {
            await navigateTo({ path: '/login', query: { action: 'favorite' } })
            return;
        }
        try {
            busy.value = true;
            isFavorite.value = false;
            await $fetch('/api/v1/user/unfavorite', {
                method: 'POST',
                body: { sessionId: props.session.unique_id, email: user.value.email, ticketId: user.value.ticketId },
            });
            await refreshSession();
        } catch (error) {
            window.alert('There was an error adding this session to your favorites. Please try again later.');
            console.error('Error favoriting session:', error);
            isFavorite.value = null;
        } finally {
            busy.value = false;
        }
    };

    const handleReservationClick = async () => {
        await navigateTo({ path: `/reservations/${props.session.id}` });
        return;
    };

    const button = {
        url: '/register',
        label: 'Register Now',
    };

    const buttonStyles = {
        base: 'bg-transparent border-teal-light text-teal-light font-medium text-[0.9375rem] tracking-[-0.01875rem] hover:text-orange-dark hover:bg-transparent',
        leadingIcon: 'mr-2',
    };
</script>

<template>
    <div class="agenda-page" v-if="session">
        <div class="block block-agenda-header" :class="{ 'has-reservation': session.reservations_required, 'has-speakers': session.speakers && session.speakers.length > 0 }">
            <div class="container">
                <ProjectBreadcrumbs light />
                <span class="required-reservation" v-if="session.reservations_required">Reservation Required</span>
                <div class="header-wrapper">
                    <div class="header">
                        <p class="time" v-if="session.start_date_and_time">
                            <span class="date">
                                <template v-for="(part, index) in session.display_date">
                                    {{ part }}
                                    <template v-if="duration && index === session.display_date.length - 1">({{ duration }})</template>
                                    <span v-if="index < session.display_date.length" class="bullet">&bull;</span>
                                </template>
                            </span>
                            <span v-if="location" class="location">
                                <span v-html="location.name"></span>
                                <span v-if="location.description" class="bullet">&bull;</span>
                                <span v-if="location.description" v-html="location.description"></span>
                            </span>
                        </p>
                        <h1 v-html="session.title" class="h1"></h1>
                        <p v-html="session.description" class="description"></p>
                        <p
                            class="skill-level"
                            v-if="session.skill_level"
                            @click.prevent="handleSkillClick(session.skill_level)"
                            v-html="session.skill_level"
                        ></p>
                        <SessionsTags :session="session" />
                        <div class="powered-and-share">
                            <FragmentsSponsorsSponsoredBy :sponsors="session.sponsors" />
                            <FragmentsSponsorsAlsoSponsoredBy :sponsors="session.also_sponsors" />
                            <div class="tags-share">
                                <slot name="share">
                                    <FragmentsShare :title="session.title" copyToClipboard />
                                </slot>
                            </div>
                        </div>
                    </div>
                    <div class="actions" v-if="session.unique_id">
                        <UButton v-if="is_favorited" variant="outline" icon="local-heart-filled" :ui="buttonStyles" @click.prevent="handleUnfavoriteClick">Favorited</UButton>
                        <UButton v-else-if="session.unique_id" variant="outline" icon="local-heart-empty" :ui="buttonStyles" @click.prevent="handleFavoriteClick">Add to Favorites</UButton>
                        <UButton v-if="session.unique_id && session.reservations_required" variant="outline" icon="local-seat-empty" :ui="buttonStyles" @click="handleReservationClick">Save a Seat</UButton>
                    </div>
                </div>
                <SessionsSpeakers :session="session"></SessionsSpeakers>
            </div>
        </div>
        <SessionsRelated :session="session"></SessionsRelated>
        <ImageTextCta
            headline="Your Growth Moment is Here"
            copy="San Francisco is calling. Join fellow change-makers for three high-impact days of powerful insights and real connections. Leave with proven tactics and strategies that drive results."
            :image="image"
            :button="button"
        />
    </div>
</template>

<style lang="scss">
    .block.block-agenda-header {
        margin-top: calc(var(--header-height) * -1);
        padding-top: calc(var(--header-height) + 3rem);
        padding-bottom: var(--box-padding-tb-m);
        color: var(--teal);
        background-color: var(--cream);

        @media (min-width: $tablet) {
            padding-top: calc(var(--header-height) + 6.7375rem);
            background-image: url('@/assets/img/backgrounds/bubble-cream-14.png');
            background-position: right top;
            background-repeat: no-repeat;
            margin-bottom: 0;
        }

        &.has-speakers {
            @media (min-width: $tablet) {
                background-image: url('@/assets/img/backgrounds/bubble-cream-14.png'), url('@/assets/img/backgrounds/bubble-cream-15.png');
                background-position: right top, left bottom;
                background-repeat: no-repeat, no-repeat;
            }
        }
        
        .block-session-speakers {
            @media (min-width: $tablet) {
                margin-top: 0;
            }
        }

        .required-reservation {
            width: fit-content;
            display: flex;
            color: white;
            font-size: 0.75rem;
            font-weight: 500;
            line-height: 1.1875rem;
            letter-spacing: 0.0375rem;
            text-transform: uppercase;
            padding: 0.375rem 1rem;
            background-color: var(--orange-dark);
            border-radius: var(--full-radius);
            margin-bottom: 1.5625rem;
        }

        .header-wrapper {
            display: flex;
            flex-direction: column;

            @media (min-width: $tablet) {
                flex-direction: row;
                gap: 2.5rem;
                align-items: flex-start;
                justify-content: space-between;
            }
        }

        .actions {
            background-color: white;
            display: flex;
            flex-direction: column;
            width: 100%;
            border-radius: var(--radius);
            gap: 1rem;
            padding: var(--box-padding-tb-m);

            @media (min-width: $tablet) {
                padding: 2.75rem;
                max-width: 21.875rem;
            }
        }

        .header {
            width: 100%;
            max-width: 47.0625rem;
            margin-bottom: 3rem;

            @media (min-width: $tablet) {
                margin-bottom: 0;
            }

            h1 {
                font-size: calc(38 / var(--base-fs) * 1rem);
                font-weight: 500;
                line-height: 2.5rem;
                letter-spacing: -0.10875rem;
                margin-bottom: 1.25rem;

                @media (min-width: $tablet) {
                    font-size: calc(58 / var(--base-fs) * 1rem);
                    line-height: 4rem;
                }
            }

            .time {
                display: flex;
                flex-direction: column;
                align-items: center;
                font-size: 0.9375rem;
                font-weight: 500;
                line-height: 1.625rem;
                margin-bottom: 1.25rem;

                .date {
                    display: flex;
                    gap: 0.5rem;
                    align-items: center;
                }

                @media (min-width: $tablet) {
                    flex-direction: row;
                    gap: 0.5rem;
                }

                span.bullet {
                    color: var(--teal-light);
                    &:last-child {
                        display: none;
                    }
                }

                .location {
                    display: inline-flex;
                    gap: 0.5rem;
                    color: var(--teal-light);
                    font-size: 0.75rem;
                    font-style: normal;
                    font-weight: 500;
                    line-height: 1.1875rem;
                    text-transform: uppercase;

                    @media (min-width: $tablet) {
                        display: inline-flex;
                        margin-left: 2rem;
                    }
                }
            }

            .description {
                font-size: calc(17 / var(--base-fs) * 1rem);
                font-weight: 300;
                line-height: 1.75rem;
                letter-spacing: 0.02125rem;
                margin-bottom: 2rem;
            }

            .session-tags-list {
                margin-bottom: 3rem;
                .fs-12 {
                    letter-spacing: 0.0375rem;
                }
            }

            .skill-level {
                width: fit-content;
                font-size: 0.6875rem;
                font-weight: 400;
                line-height: 0.6875rem;
                letter-spacing: 0.034375rem;
                text-transform: uppercase;
                margin-bottom: 1.875rem;
                padding: 0.4375rem 0.75rem 0.4375rem 0.75rem;
                border-radius: var(--radius);
                border: 1px solid var(--teal-darkish);
                cursor: pointer;

                @include hover {
                    background-color: var(--teal-darkish);
                    color: white;
                }
            }

            .powered-and-share {
                display: flex;
                flex-direction: column;
                width: 100%;

                .sponsored-by {
                    margin-bottom: 0rem;
                }

                @media (min-width: $tablet) {
                    flex-direction: row;
                    align-items: center;

                    .sponsored-by {
                        margin-top: 0;
                        margin-right: 3.25rem;
                    }
                }
            }
        }
    }
</style>
